'use client';

import throttle from 'lodash.throttle';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
interface ScrollContextType {
  isScrollingUp: boolean;
  scrollY: number;
}
const ScrollContext = createContext<ScrollContextType | undefined>(undefined);
const DELAY = 300;
export const ScrollProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [isScrollingUp, setIsScrollingUp] = useState(true);
  const [currentScroll, setCurrentScroll] = useState(typeof window !== 'undefined' ? window.scrollY : 0);
  const lastScrollYRef = useRef(0);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrollingUp(currentScrollY <= lastScrollYRef.current);
      lastScrollYRef.current = currentScrollY;
      setCurrentScroll(currentScrollY);
    };
    const debouncedHandleScroll = throttle(handleScroll, DELAY);
    window.addEventListener('scroll', debouncedHandleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, []);
  const providerValue = useMemo(() => ({
    isScrollingUp,
    scrollY: currentScroll
  }), [isScrollingUp, currentScroll]);
  return <ScrollContext.Provider value={providerValue} data-sentry-element="unknown" data-sentry-component="ScrollProvider" data-sentry-source-file="use-scroll.tsx">{children}</ScrollContext.Provider>;
};
export const useScroll = (): ScrollContextType => {
  const context = useContext(ScrollContext);
  if (!context) {
    throw new Error('useScroll must be used within a ScrollProvider');
  }
  return context;
};